import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

function EditCityCompanymanagement({
    selectedCountry,
    selectedState,
    selectedCities,
    onClose,
    onSave
}) {
    const [cities, setCities] = useState([...selectedCities]);
    const [newCity, setNewCity] = useState('');
    const [cityOptions, setCityOptions] = useState([]);
    const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const cityDropdownRef = useRef(null);
    const inputRef = useRef(null);

    const GEONAMES_USERNAME = 'atom23';
    const GEONAMES_API = 'https://secure.geonames.org';

    const fetchCities = async () => {
        if (!selectedCountry?.value || !selectedState?.label) {
            setCityOptions([]);
            return;
        }

        setLoadingCities(true);
        try {
            // First try direct city search by state name
            const directSearch = await axios.get(
                `${GEONAMES_API}/searchJSON?q=${encodeURIComponent(selectedState.label)}&country=${selectedCountry.value}&featureClass=P&maxRows=50&username=${GEONAMES_USERNAME}`
            );

            let citiesData = [];
            if (directSearch?.data?.geonames?.length > 0) {
                citiesData = directSearch.data.geonames.map(place => ({
                    value: place.name,
                    label: place.name
                }));
            }

            // If no results, try getting children of the country
            if (citiesData.length === 0) {
                const countryInfo = await axios.get(
                    `${GEONAMES_API}/countryInfoJSON?country=${selectedCountry.value}&username=${GEONAMES_USERNAME}`
                );
                
                if (countryInfo?.data?.geonames?.length > 0) {
                    const countryGeonameId = countryInfo.data.geonames[0].geonameId;
                    const childrenResponse = await axios.get(
                        `${GEONAMES_API}/childrenJSON?geonameId=${countryGeonameId}&username=${GEONAMES_USERNAME}`
                    );
                    
                    if (childrenResponse?.data?.geonames?.length > 0) {
                        citiesData = childrenResponse.data.geonames
                            .filter(place => place.fcode.startsWith('PPL'))
                            .map(place => ({
                                value: place.name,
                                label: place.name
                            }));
                    }
                }
            }

            setCityOptions(citiesData);
            
            if (citiesData.length === 0) {
                toast.info('No city suggestions found. You can add cities manually.');
            }
        } catch (error) {
            console.warn('City suggestions service unavailable:', error.message);
            toast.info('City suggestions service unavailable. You can add cities manually.');
            setCityOptions([]);
        } finally {
            setLoadingCities(false);
        }
    };

    useEffect(() => {
        fetchCities();
    }, [selectedCountry, selectedState]);
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
                setIsCityDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleAddCity = () => {
        if (!newCity.trim()) {
            toast.error('Please enter a city name');
            return;
        }
        
        const cityExists = cities.some(c => c.label.toLowerCase() === newCity.trim().toLowerCase());
        if (cityExists) {
            toast.error('City already exists');
            return;
        }

        setCities([...cities, {
            value: newCity.trim(),
            label: newCity.trim()
        }]);
        setNewCity('');
        setIsCityDropdownOpen(false);
    };

    const handleSelectCity = (city) => {
        const cityExists = cities.some(c => c.value === city.value);
        if (!cityExists) {
            setCities([...cities, city]);
        }
        setNewCity('');
        setIsCityDropdownOpen(false);
    };

    const handleRemoveCity = (cityToRemove) => {
        setCities(cities.filter(city => city.value !== cityToRemove.value));
    };

    const handleSave = () => {
        if (cities.length === 0) {
            toast.error('Please add at least one city');
            return;
        }
        onSave(cities);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddCity();
        }
    };

    const toggleCityDropdown = () => {
        setIsCityDropdownOpen(!isCityDropdownOpen);
        if (!isCityDropdownOpen) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    return (
        <div className="w-[600px] max-w-full bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Edit Selection</h2>

            <div className="mb-4">
                <label className="block text-gray-600 font-medium mb-1">Country:</label>
                <div className="text-gray-900 p-2 bg-gray-50 rounded">{selectedCountry?.label || '-'}</div>
            </div>

            <div className="mb-4">
                <label className="block text-gray-600 font-medium mb-1">State:</label>
                <div className="text-gray-900 p-2 bg-gray-50 rounded">{selectedState?.label || '-'}</div>
            </div>

            <div className="mb-6">
                <label className="block text-gray-600 font-medium mb-1">Cities:</label>
                
                <div className="relative mb-2" ref={cityDropdownRef}>
                    <div className="flex">
                        <input
                            ref={inputRef}
                            type="text"
                            value={newCity}
                            onChange={(e) => setNewCity(e.target.value)}
                            onKeyPress={handleKeyPress}
                            onFocus={() => setIsCityDropdownOpen(true)}
                            placeholder="Search or add city"
                            className="flex-1 p-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            onClick={toggleCityDropdown}
                            className="p-2 border border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                            {isCityDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                        <button
                            onClick={handleAddCity}
                            className="bg-blue-500 text-white px-4 rounded-r-md hover:bg-blue-600 transition"
                        >
                            <Plus size={20} />
                        </button>
                    </div>
                    
                    {isCityDropdownOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {loadingCities ? (
                                <div className="p-2 text-gray-500">Loading cities...</div>
                            ) : cityOptions.length > 0 ? (
                                <>
                                    {cityOptions.map((city) => (
                                        <div
                                            key={city.value}
                                            className="p-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                                            onClick={() => handleSelectCity(city)}
                                        >
                                            <span>{city.label}</span>
                                            {cities.some(c => c.value === city.value) ? (
                                                <X
                                                    className="text-red-500 hover:text-red-700"
                                                    size={16}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveCity(city);
                                                    }}
                                                />
                                            ) : (
                                                <Plus className="text-green-500" size={16} />
                                            )}
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <div className="p-2 text-gray-500">
                                    {!selectedState ? "Select state first" : "No cities found"}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2">
                    {cities.length > 0 ? (
                        cities.map((city, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-2 bg-white rounded-lg shadow-sm border border-gray-200 transition-all hover:border-blue-300"
                            >
                                <span className="text-sm text-gray-800">{city.label}</span>
                                <button
                                    onClick={() => handleRemoveCity(city)}
                                    className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
                                    aria-label="Delete city"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full text-center text-gray-500 py-4">
                            No cities added yet
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end space-x-3">
                <button
                    onClick={onClose}
                    className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition"
                >
                    Update
                </button>
            </div>
        </div>
    );
}

export default EditCityCompanymanagement;

// import React, { useState } from 'react';
// import { X, Plus } from 'lucide-react';
// import { toast } from 'react-toastify';

// function EditCityCompanymanagement({
//     selectedCountry,
//     selectedState,
//     selectedCities,
//     onClose,
//     onSave
// }) {
//     const [cities, setCities] = useState([...selectedCities]);
//     const [newCity, setNewCity] = useState('');

//     const handleAddCity = () => {
//         if (!newCity.trim()) {
//             toast.error('Please enter a city name');
//             return;
//         }
        
//         const cityExists = cities.some(c => c.label.toLowerCase() === newCity.trim().toLowerCase());
//         if (cityExists) {
//             toast.error('City already exists');
//             return;
//         }

//         setCities([...cities, {
//             value: newCity.trim(),
//             label: newCity.trim()
//         }]);
//         setNewCity('');
//     };

//     const handleRemoveCity = (cityToRemove) => {
//         setCities(cities.filter(city => city.value !== cityToRemove.value));
//     };

//     const handleSave = () => {
//         if (cities.length === 0) {
//             toast.error('Please add at least one city');
//             return;
//         }
//         onSave(cities);
//     };

//     const handleKeyPress = (e) => {
//         if (e.key === 'Enter') {
//             handleAddCity();
//         }
//     };

//     return (
//         <div className="w-[600px] max-w-full bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
//             <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Edit Selection</h2>

//             <div className="mb-4">
//                 <label className="block text-gray-600 font-medium mb-1">Country:</label>
//                 <div className="text-gray-900 p-2 bg-gray-50 rounded">{selectedCountry?.label || '-'}</div>
//             </div>

//             <div className="mb-4">
//                 <label className="block text-gray-600 font-medium mb-1">State:</label>
//                 <div className="text-gray-900 p-2 bg-gray-50 rounded">{selectedState?.label || '-'}</div>
//             </div>

//             <div className="mb-6">
//                 <label className="block text-gray-600 font-medium mb-1">Cities:</label>
                
//                 <div className="flex mb-2">
//                     <input
//                         type="text"
//                         value={newCity}
//                         onChange={(e) => setNewCity(e.target.value)}
//                         onKeyPress={handleKeyPress}
//                         placeholder="Add new city"
//                         className="flex-1 p-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                     <button
//                         onClick={handleAddCity}
//                         className="bg-blue-500 text-white px-4 rounded-r-md hover:bg-blue-600 transition"
//                     >
//                         <Plus size={20} />
//                     </button>
//                 </div>
                
//                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2">
//                     {cities.length > 0 ? (
//                         cities.map((city, index) => (
//                             <div
//                                 key={index}
//                                 className="flex items-center justify-between p-2 bg-white rounded-lg shadow-sm border border-gray-200 transition-all hover:border-blue-300"
//                             >
//                                 <span className="text-sm text-gray-800">{city.label}</span>
//                                 <button
//                                     onClick={() => handleRemoveCity(city)}
//                                     className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
//                                     aria-label="Delete city"
//                                 >
//                                     <X size={16} />
//                                 </button>
//                             </div>
//                         ))
//                     ) : (
//                         <div className="col-span-full text-center text-gray-500 py-4">
//                             No cities added yet
//                         </div>
//                     )}
//                 </div>
//             </div>

//             <div className="flex justify-end space-x-3">
//                 <button
//                     onClick={onClose}
//                     className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
//                 >
//                     Cancel
//                 </button>
//                 <button
//                     onClick={handleSave}
//                     className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition"
//                 >
//                     Update
//                 </button>
//             </div>
//         </div>
//     );
// }

// export default EditCityCompanymanagement;

// import React, { useState } from 'react';

// function EditCityCompanymanagement({
//     selectedCountry,
//     selectedState,
//     selectedCities,
//     onClose
// }) {

//     const [city, setcity] = useState(selectedCities)
//     const removecity = () => {
//         setcity([])
//     }

//     // console.log(`this is cities ${JSON.stringify(selectedCities)}`);

//     return (
//         <div className="w-[600px] max-w-full bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
//             <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Edit Selection</h2>

//             <div className="mb-4 flex">
//                 <label className="block text-gray-600 font-medium mb-1">Country:</label>
//                 <div className="text-gray-900">{selectedCountry?.label || '-'}</div>
//             </div>

//             <div className="mb-4 flex">
//                 <label className="block text-gray-600 font-medium mb-1">State:</label>
//                 <div className="text-gray-900">{selectedState?.label || '-'}</div>
//             </div>

//             <div className="mb-6">
//                 <label className="block text-gray-600 font-medium mb-1">Cities:</label>
//                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
//                     {selectedCities.length > 0 ? (
//                         selectedCities.map((city, index) => (
//                             <div
//                                 key={index}
//                                 className="flex items-center justify-between p-1 bg-white rounded-lg shadow-sm border border-gray-200 transition-all group hover:border-blue-300 hover:shadow-lg"
//                             >
//                                 <span className="text-sm text-gray-800">{city.label}</span>
//                                 <button
//                                     className="p-1 text-xs text-red-500 hover:text-red-700 rounded-full border border-gray-300  "
//                                     aria-label="Delete city"
//                                     onClick={removecity}
//                                 >
//                                     ❌
//                                 </button>
//                             </div>
//                         ))
//                     ) : (
//                         <span className="text-gray-600">No cities available</span>
//                     )}
//                 </div>
//             </div>



//             <div className="flex justify-end">
//                 <button
//                     onClick={onClose}
//                     className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition"
//                 >
//                     Close
//                 </button>
//             </div>
//         </div>
//     );
// }

// export default EditCityCompanymanagement;
